import { ClerkProvider } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import { DevBypassTrpcProvider, TrpcProvider } from "./TrpcProvider";
import { AuthProvider, DevBypassAuthProvider } from "./auth/AuthContext";
import { CHAPTER } from "./chapter";
import { DEV_BYPASS } from "./devBypass";
import { initSentry } from "./sentry";
import "./index.css";

// Error + trace reporting. Safe in dev — no-op without a DSN.
initSentry();

// index.html is static and shared by every chapter build; the tab
// title (and the name a phone gives the page on its home screen) is
// the first place the chapter shows.
document.title = `${CHAPTER.name} team app`;

/**
 * What a volunteer sees if a page crashes. Sentry has already reported
 * it; the person at the door needs to know that reloading is safe and
 * that nothing they did is lost.
 */
function CrashScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[color:var(--color-bg)] px-5 py-10">
      <div role="alert" className="surface w-full max-w-md p-6 text-center">
        <h1 className="text-[22px] leading-snug font-bold text-[color:var(--color-ink)]">
          Something went wrong on this page.
        </h1>
        <p className="mt-3 text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
          Reload to try again. Check-ins you&apos;ve already made are saved.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full bg-[color:var(--color-ink)] px-6 text-[15px] font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-85"
        >
          Reload
        </button>
      </div>
    </div>
  );
}

const root = document.getElementById("root");
if (!root) {
  throw new Error("Missing #root element");
}

if (DEV_BYPASS) {
  // Local development without Clerk keys: no ClerkProvider anywhere in
  // this tree (its hooks would throw), no publishable key needed. The
  // local Worker's localhost-gated bypass supplies the admin identity.
  // DEV_BYPASS is compile-time false in production builds.
  createRoot(root).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={<CrashScreen />}>
        <DevBypassTrpcProvider>
          <DevBypassAuthProvider>
            <App />
          </DevBypassAuthProvider>
        </DevBypassTrpcProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
} else {
  const publishableKey = import.meta.env.VITE_ADMIN_CLERK_PUBLISHABLE_KEY;
  if (!publishableKey) {
    throw new Error(
      "Add VITE_ADMIN_CLERK_PUBLISHABLE_KEY to the .env file or CI build env.",
    );
  }

  createRoot(root).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={<CrashScreen />}>
        <ClerkProvider
          publishableKey={publishableKey}
          afterSignOutUrl="/"
          signInFallbackRedirectUrl="/"
          signUpFallbackRedirectUrl="/"
        >
          {/* TrpcProvider wraps AuthProvider so the auth context can call
              adminMe — role is resolved server-side from the verified
              Clerk session, not from anything available client-side. */}
          <TrpcProvider>
            <AuthProvider>
              <App />
            </AuthProvider>
          </TrpcProvider>
        </ClerkProvider>
      </Sentry.ErrorBoundary>
    </StrictMode>,
  );
}
